import React, {useContext} from "react";
import SectionCard from "../common/SectionCard";
import DetailGrid from "../common/DetailGrid";
import {UserOutlined, MailOutlined, EditOutlined} from "@ant-design/icons";
import {MapPin} from "lucide-react";
import {Button} from "antd";
import {useLocation, useNavigate} from "react-router-dom";
import UserLoginContext from "../../../../context/userLoginContext";
import {
    capitalizeString, firstLetterUpperCase,
    formatBloodGroup,
} from "../../../../utility/utility";
import Locale from "../../../../core/locale/locale";
import dayjs from "dayjs";
import CopyContent from "../../../../common/copy-content";

const PersonalInfoTab = ({employeeData}: any) => {
    const navigate = useNavigate();
    const location = useLocation();
    const {user} = useContext(UserLoginContext);
    const isActiveEmployee = location.state?.isActive ?? true;
    const navigateToPersonalDetail = () => navigate(`/employee/edit/personal`, {state: {empId: employeeData.id}});
    const navigateToContactDetail = () => navigate(`/employee/edit/contact`, {state: {empId: employeeData.id}});


    return (
        <>
            <div className={'personal-contact-card'}>
                <SectionCard
                    icon={<UserOutlined style={{background: " #dbeafe", color: '#1d4ed8'}}/>}
                    title="Personal Details"
                    extra={
                        user?.isAdmin && isActiveEmployee && (
                            <Button type={"link"} onClick={() => navigateToPersonalDetail()}> <EditOutlined/> </Button>
                        )
                    }
                >
                    <DetailGrid
                        data={[
                            {label: "First Name", value: <CopyContent content={employeeData?.name.firstName}>
                                        {employeeData?.name.firstName}
                                </CopyContent>},
                            {label: "Last Name", value: employeeData?.name?.lastName},
                            {
                                label: "Gender",
                                value: employeeData?.gender
                                    ? firstLetterUpperCase(employeeData?.gender)
                                    : "-",
                            },
                            {
                                label: "Marital Status", value: employeeData?.maritalStatus
                                    ? firstLetterUpperCase(employeeData?.maritalStatus)
                                    : "-"
                            },
                            {
                                label: "Blood Group", value: employeeData?.bloodGroup
                                    ? formatBloodGroup(employeeData?.bloodGroup)
                                    : "-"
                            },
                            {
                                label: "Date of Birth",
                                value: employeeData?.dateOfBirth
                                    ? dayjs(employeeData.dateOfBirth).format(Locale.displayDate)
                                    : "-"
                            }
                        ]}
                    />
                </SectionCard>

                <SectionCard
                    icon={<MailOutlined style={{background: "#fef3c7", color: "#b45309"}}/>}
                    title="Contact Details"
                    extra={
                        user?.isAdmin && isActiveEmployee && (
                            <Button type={"link"} onClick={() => navigateToContactDetail()}> <EditOutlined/> </Button>
                        )}
                >
                    <DetailGrid
                        data={[
                            {
                                label: "Official Email", value: employeeData?.officialEmail
                                    ? <CopyContent content={employeeData?.officialEmail}>
                                        <a
                                            href={`https://mail.google.com/mail/?view=cm&fs=1&to=${employeeData?.officialEmail}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="email-link"
                                        >
                                            {employeeData?.officialEmail}
                                        </a>
                                    </CopyContent>
                                    : "-"
                            },
                            {
                                label: "Personal Email", value: employeeData?.email
                                    ? <CopyContent content={employeeData?.email}>
                                        <a
                                            href={`https://mail.google.com/mail/?view=cm&fs=1&to=${employeeData?.email}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="email-link"
                                        >
                                            {employeeData?.email}
                                        </a>
                                    </CopyContent>
                                    : "-"
                            },
                            {
                                label: "Primary Mobile Number",
                                value: employeeData?.mobileNumber?.number ? (
                                    <CopyContent
                                        content={`${employeeData?.mobileNumber?.countryCode ?? ""}${employeeData?.mobileNumber?.number}`}
                                    >
                                        <a
                                            href={`tel:${employeeData?.mobileNumber?.countryCode ?? ""}${employeeData?.mobileNumber?.number}`}
                                            className="email-link"
                                        >
                                            {`${employeeData?.mobileNumber?.countryCode ?? ""} ${employeeData?.mobileNumber?.number}`}
                                        </a>
                                    </CopyContent>
                                ) : (
                                    "-"
                                )
                            },
                            {
                                label: "Alternate Mobile Number",
                                value: employeeData?.alternateMobileNumber?.number ? (
                                    <CopyContent
                                        content={`${employeeData?.mobileNumber?.countryCode ?? ""}${employeeData?.alternateMobileNumber?.number}`}
                                    >
                                        <a
                                            href={`tel:${employeeData?.mobileNumber?.countryCode ?? ""}${employeeData?.alternateMobileNumber?.number}`}
                                            className="email-link"
                                        >
                                            {`${employeeData?.mobileNumber?.countryCode ?? ""} ${employeeData?.alternateMobileNumber?.number}`}
                                        </a>
                                    </CopyContent>
                                ) : (
                                    "-"
                                )
                            },
                        ]}
                    />
                </SectionCard>
            </div>

            <SectionCard
                title="Addresses"
                icon={<MapPin className={'employee-detail-lucide location-icon'}/>}
                extra={
                    user?.isAdmin && isActiveEmployee && (
                        <Button type={"link"} onClick={() => navigateToContactDetail()}> <EditOutlined/> </Button>
                    )}
            >
                <div className="employee-detail-address-wrapper">

                    {/* Present Address */}
                    <div className="address-card">
                        <div className="address-header">
                            <span className="address-dot present"></span>
                            <span className="address-title">PRESENT ADDRESS</span>
                        </div>
                        {
                            employeeData?.presentAddress ?
                                <div className="address-content">
                                    {employeeData?.presentAddress?.line1}<br/>
                                    {employeeData?.presentAddress?.line2 && (
                                        <>
                                            {employeeData?.presentAddress?.line2}<br/>
                                        </>
                                    )}
                                    {employeeData?.presentAddress?.city}, {employeeData?.presentAddress?.state} {employeeData?.presentAddress?.zipCode}<br/>
                                    {employeeData?.presentAddress?.country}
                                </div>
                                :
                                <div>No data provided </div>
                        }

                    </div>

                    {/* Permanent Address */}
                    <div className="address-card">
                        <div className="address-header">
                            <span className="address-dot permanent"></span>
                            <span className="address-title">PERMANENT ADDRESS</span>
                        </div>
                        {
                            employeeData?.permanentAddress ?
                                <div className="address-content">
                                    {employeeData?.permanentAddress?.line1}<br/>
                                    {
                                        employeeData?.permanentAddress?.line2 && (
                                            <>
                                                {employeeData?.permanentAddress?.line2}<br/>
                                            </>
                                        )
                                    }
                                    {employeeData?.permanentAddress?.city}, {employeeData?.permanentAddress?.state} {employeeData?.permanentAddress?.zipCode}<br/>
                                    {employeeData?.permanentAddress?.country}
                                </div>
                                :
                                <div>No data provided </div>
                        }

                    </div>

                </div>
            </SectionCard>
        </>
    );
};

export default PersonalInfoTab;
